import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Navbar from '../components/Navbar';
import CategoryBar from '../components/CategoryBar';
import ExperienceCard from '../components/ExperienceCard';
import ExperienceMap from '../components/ExperienceMap';
import Footer from '../components/Footer';
import { useCurrency } from '../context/CurrencyContext';
import axios from 'axios';
import { Loader2, LayoutGrid, Map, Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Reviews Carousel ─── */
const ReviewsCarousel = ({ reviews }) => {
  const scrollRef = useRef(null);
  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 360, behavior: 'smooth' });
  };
  if (!reviews.length) return null;

  return (
    <section className="bg-white py-14 px-6 md:px-10 lg:px-20 overflow-hidden" data-testid="reviews-section">
      <div className="max-w-[2000px] mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-sm font-semibold text-[#D65A31] uppercase tracking-wide mb-1">What our diners say</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Guest reviews</h2>
          </div>
          <div className="hidden md:flex gap-2">
            <button onClick={() => scroll(-1)} className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors" data-testid="reviews-scroll-left">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => scroll(1)} className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors" data-testid="reviews-scroll-right">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div ref={scrollRef} className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {reviews.map((r) => (
            <div key={r.id || `${r.user_name}-${r.experience_title}`} className="min-w-[320px] max-w-[340px] snap-start bg-[#FDF8F4] rounded-xl p-6 flex flex-col shrink-0" data-testid={`review-card-${r.id}`}>
              <Quote className="w-6 h-6 text-[#D65A31] opacity-40 mb-3 -scale-x-100" />
              <p className="text-gray-700 text-sm leading-relaxed flex-1 mb-4 line-clamp-4">{r.comment}</p>
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className={`w-4 h-4 ${s < r.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                ))}
              </div>
              <div className="border-t pt-3">
                <p className="font-semibold text-gray-900 text-sm">{r.user_name}</p>
                <p className="text-xs text-gray-500 truncate">{r.experience_title} &middot; {r.experience_location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Location Section ─── */
const LocationSection = ({ title, location, experiences }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, [checkScroll, experiences]);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.querySelector('div')?.offsetWidth || 240;
    scrollRef.current.scrollBy({ left: dir * (cardWidth + 16) * 2, behavior: 'smooth' });
  };

  return (
    <section className="mb-14" data-testid={`location-section-${title.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">{title}</h2>
          <a
            href={`/search?location=${encodeURIComponent(location)}`}
            className="text-sm font-medium text-gray-600 hover:text-[#D65A31] hover:underline whitespace-nowrap"
            data-testid={`view-all-${location.split(',')[0].trim().toLowerCase()}`}
          >
            &rarr;
          </a>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => scroll(-1)}
            disabled={!canScrollLeft}
            className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors ${canScrollLeft ? 'border-gray-800 bg-white text-gray-800 hover:bg-gray-800 hover:text-white shadow-md' : 'border-gray-300 bg-gray-100 text-gray-300 cursor-default'}`}
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => scroll(1)}
            disabled={!canScrollRight}
            className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors ${canScrollRight ? 'border-gray-800 bg-white text-gray-800 hover:bg-gray-800 hover:text-white shadow-md' : 'border-gray-300 bg-gray-100 text-gray-300 cursor-default'}`}
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto w-full"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {experiences.map((exp) => (
          <div key={exp.id || exp._id} className="shrink-0 w-[280px]">
            <ExperienceCard experience={exp} />
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─── Main Page ─── */

const HomePage = () => {
  const { formatPrice } = useCurrency();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    priceRange: [0, 550],
    cuisines: [],
    mealTypes: [],
    locations: [],
  });
  const [viewMode, setViewMode] = useState('grid');
  const [reviews, setReviews] = useState([]);

  const fetchExperiences = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (filters.priceRange[0] > 0) params.append('min_price', filters.priceRange[0]);
      if (filters.priceRange[1] < 550) params.append('max_price', filters.priceRange[1]);
      if (filters.cuisines.length === 1) params.append('cuisine', filters.cuisines[0]);

      const response = await axios.get(`${API_URL}/api/experiences?${params.toString()}`);
      let data = response.data;

      // Client-side filtering for multi-select fields the API doesn't support
      if (filters.cuisines.length > 1) {
        data = data.filter(exp => filters.cuisines.includes(exp.cuisine));
      }
      if (filters.mealTypes.length > 0) {
        data = data.filter(exp => filters.mealTypes.includes(exp.meal_type));
      }
      if (filters.locations.length > 0) {
        data = data.filter(exp => filters.locations.includes(exp.location));
      }

      setExperiences(data);
    } catch (err) {
      console.error('Failed to fetch experiences:', err);
      setError('Failed to load experiences. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, filters]);

  useEffect(() => {
    fetchExperiences();
  }, [fetchExperiences]);

  useEffect(() => {
    axios.get(`${API_URL}/api/reviews/featured?limit=20`).then(res => setReviews(res.data)).catch(() => {});
  }, []);

  /* Group experiences by location — London first, then Dubai */
  const locationGroups = useMemo(() => {
    const targetLocations = ['london', 'dubai'];
    // Home-hosted categories first, tours/classes last
    const categoryOrder = {
      'Home Cooked': 0,
      'Supper Club': 1,
      'Aperitivo': 2,
      'BBQ & Grill': 3,
      'Street Food': 4,
      'Farm to Table': 5,
      'Cooking Class': 6,
      'Food Tour': 7,
    };
    const groups = {};
    experiences.forEach(exp => {
      const loc = exp.location || 'Other';
      const locLower = loc.toLowerCase();
      if (targetLocations.some(t => locLower.includes(t))) {
        if (!groups[loc]) groups[loc] = [];
        groups[loc].push(exp);
      }
    });
    // Sort experiences within each group: home-hosted first
    Object.values(groups).forEach(exps => {
      exps.sort((a, b) => (categoryOrder[a.category] ?? 99) - (categoryOrder[b.category] ?? 99));
    });
    // Sort groups: London first, then Dubai
    return Object.entries(groups).sort((a, b) => {
      const aLon = a[0].toLowerCase().includes('london') ? 0 : 1;
      const bLon = b[0].toLowerCase().includes('london') ? 0 : 1;
      return aLon - bLon;
    });
  }, [experiences]);

  /* Find the midpoint for inserting the reviews carousel */
  const midIndex = Math.ceil(locationGroups.length / 2);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const activeFilterCount =
    (filters.priceRange[0] > 0 || filters.priceRange[1] < 550 ? 1 : 0) +
    filters.cuisines.length +
    filters.mealTypes.length +
    filters.locations.length;

  const filterSummary = [];
  if (filters.cuisines.length > 0) filterSummary.push(filters.cuisines.join(', '));
  if (filters.mealTypes.length > 0) filterSummary.push(filters.mealTypes.join(', '));
  if (filters.locations.length > 0) filterSummary.push(filters.locations.join(', '));
  if (filters.priceRange[0] > 0 || filters.priceRange[1] < 550) {
    filterSummary.push(`${formatPrice(filters.priceRange[0])}–${formatPrice(filters.priceRange[1])}`);
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EDE4] overflow-x-hidden" data-testid="home-page">
      <Navbar />
      <div className="h-20" />

      <CategoryBar
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
        onFiltersChange={handleFiltersChange}
        activeFilterCount={activeFilterCount}
      />

      {/* Hero Section - hidden in map view */}
      {viewMode !== 'map' && (
        <div className="relative text-white py-20 md:py-28 px-6 md:px-10 lg:px-20 overflow-hidden" style={{ minHeight: '280px' }}>
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/6954047/pexels-photo-6954047.jpeg?auto=compress&cs=tinysrgb&w=1920&h=600&fit=crop"
              alt="Friends sharing a meal together"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/55" />
          </div>
          <div className="relative max-w-[900px] mx-auto flex flex-col items-center text-center gap-5">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              Every meal has a host.<br className="hidden sm:block" /> Every table has a story.
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-[600px]">
              Skip the tourist menu. Book a seat at a local's home table in London.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <a
                href="#experience-grid"
                className="px-7 py-3 bg-[#D65A31] hover:bg-[#B84A28] text-white font-semibold rounded-full transition-colors text-sm"
                data-testid="hero-find-btn"
              >
                Find an experience
              </a>
              <a
                href="/register?role=host"
                className="px-7 py-3 bg-white/15 hover:bg-white/25 text-white font-semibold rounded-full transition-colors text-sm border border-white/30"
                data-testid="hero-host-btn"
              >
                Host an experience
              </a>
            </div>
          </div>
        </div>
      )}

      {/* How It Works */}
      {viewMode !== 'map' && (
        <section className="bg-white py-10 px-6 md:px-10 lg:px-20 border-b" data-testid="how-it-works">
          <div className="max-w-[900px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full bg-[#FDF8F4] flex items-center justify-center text-[#D65A31] font-bold text-lg">1</div>
                <h3 className="font-semibold text-gray-900">Browse</h3>
                <p className="text-sm text-gray-500">Explore home-hosted dinners, cooking classes and supper clubs near you.</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full bg-[#FDF8F4] flex items-center justify-center text-[#D65A31] font-bold text-lg">2</div>
                <h3 className="font-semibold text-gray-900">Book</h3>
                <p className="text-sm text-gray-500">Pick a date, choose your guests and pay securely online.</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full bg-[#FDF8F4] flex items-center justify-center text-[#D65A31] font-bold text-lg">3</div>
                <h3 className="font-semibold text-gray-900">Sit down</h3>
                <p className="text-sm text-gray-500">Arrive at your host's home and enjoy an evening you'll remember.</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <main className={`max-w-[2000px] mx-auto px-6 md:px-10 lg:px-20 overflow-hidden ${viewMode === 'map' ? 'py-3' : 'flex-1 py-8'}`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold" data-testid="results-heading">
              {selectedCategory ? `${selectedCategory} experiences` : 'All experiences'}
            </h2>
            {activeFilterCount > 0 && (
              <p className="text-sm text-gray-500 mt-1" data-testid="filter-summary">
                Filtered by: {filterSummary.join(' · ')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!loading && (
              <span className="text-sm text-gray-500" data-testid="results-count">
                {locationGroups.reduce((sum, [, exps]) => sum + exps.length, 0)} result{locationGroups.reduce((sum, [, exps]) => sum + exps.length, 0) !== 1 ? 's' : ''}
              </span>
            )}
            <div className="flex border rounded-lg overflow-hidden" data-testid="view-toggle">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid="grid-view-btn"
              >
                <LayoutGrid className="w-4 h-4" /> Grid
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === 'map' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid="map-view-btn"
              >
                <Map className="w-4 h-4" /> Map
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20" data-testid="loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#D65A31]" />
            <span className="ml-3 text-gray-500">Loading experiences...</span>
          </div>
        ) : error ? (
          <div className="text-center py-20" data-testid="error">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={fetchExperiences}
              className="text-[#D65A31] font-medium hover:underline"
            >
              Try again
            </button>
          </div>
        ) : experiences.length === 0 ? (
          <div className="text-center py-20" data-testid="no-results">
            <h2 className="text-2xl font-semibold mb-2">No experiences found</h2>
            <p className="text-gray-500">
              {activeFilterCount > 0
                ? 'Try adjusting your filters or clearing them'
                : selectedCategory
                  ? 'Try selecting a different category'
                  : 'Be the first to create an experience!'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div data-testid="experience-grid">
            {/* Location sections — first half */}
            {locationGroups.slice(0, midIndex).map(([loc, exps]) => {
              const city = loc.split(',')[0];
              const isLondon = loc.toLowerCase().includes('london');
              const title = isLondon ? `Experiences in ${city}` : `Discover ${city}`;
              return <LocationSection key={loc} title={title} location={loc} experiences={exps} />;
            })}
          </div>
        ) : null}
      </main>

      {/* Reviews carousel — between location sections */}
      {viewMode === 'grid' && !loading && !error && experiences.length > 0 && (
        <ReviewsCarousel reviews={reviews} />
      )}

      {/* Second half of location sections */}
      {viewMode === 'grid' && !loading && !error && locationGroups.length > midIndex && (
        <div className="w-full max-w-[2000px] mx-auto px-6 md:px-10 lg:px-20 pb-8 pt-8 overflow-hidden">
          {locationGroups.slice(midIndex).map(([loc, exps]) => {
            const city = loc.split(',')[0];
            const isLondon = loc.toLowerCase().includes('london');
            const title = isLondon ? `Experiences in ${city}` : `Discover ${city}`;
            return <LocationSection key={loc} title={title} location={loc} experiences={exps} />;
          })}
        </div>
      )}

      {/* Map split view - rendered outside main for full width */}
      {!loading && !error && experiences.length > 0 && viewMode === 'map' && (
        <div className="flex flex-col md:flex-row overflow-hidden" style={{ height: 'calc(100vh - 220px)' }} data-testid="map-split-view">
          {/* Scrollable card list - horizontal on mobile, vertical on desktop */}
          <div className="w-full md:w-[420px] flex-shrink-0 overflow-x-auto md:overflow-y-auto border-b md:border-b-0 md:border-r bg-white p-3 md:p-4 flex md:block gap-3 md:gap-0 md:space-y-3" data-testid="map-card-list">
            {experiences.map((experience) => (
              <div key={experience.id} className="min-w-[280px] md:min-w-0">
                <ExperienceCard experience={experience} compact />
              </div>
            ))}
          </div>
          {/* Map */}
          <div className="flex-1 h-[300px] md:h-full">
            <ExperienceMap experiences={experiences} splitView />
          </div>
        </div>
      )}

      {viewMode !== 'map' && (
        <section className="bg-[#EEF5EE] py-14 px-6 md:px-10 lg:px-20" data-testid="social-section">
          <div className="max-w-[1100px] mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-2">
              Follow <span className="text-[#D65A31]">@mealhoster</span>
            </h2>
            <p className="text-center text-gray-500 italic mb-8">
              Tag us with #mealhoster and share your experience!
            </p>
            <div className="grid grid-cols-3 gap-2 md:gap-3 mb-8">
              {[
                { src: 'https://images.pexels.com/photos/5638415/pexels-photo-5638415.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop', alt: 'Outdoor dinner table' },
                { src: 'https://images.pexels.com/photos/6223232/pexels-photo-6223232.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop', alt: 'Friends toasting over dinner' },
                { src: 'https://images.pexels.com/photos/18744692/pexels-photo-18744692.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop', alt: 'Fresh spices and ingredients' },
                { src: 'https://images.pexels.com/photos/18115910/pexels-photo-18115910.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop', alt: 'Cooking class' },
                { src: 'https://images.pexels.com/photos/6223140/pexels-photo-6223140.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop', alt: 'Friends cooking together' },
                { src: 'https://images.pexels.com/photos/8841416/pexels-photo-8841416.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop', alt: 'Garden dining' },
                { src: 'https://images.pexels.com/photos/23644633/pexels-photo-23644633.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop', alt: 'Gourmet dish' },
                { src: 'https://images.pexels.com/photos/12412205/pexels-photo-12412205.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop', alt: 'Friends raising glasses' },
                { src: 'https://images.pexels.com/photos/33474124/pexels-photo-33474124.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop', alt: 'Fine dining' },
              ].map((img) => (
                <a
                  key={img.alt}
                  href="https://instagram.com/mealhoster"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-square overflow-hidden rounded-lg group"
                  data-testid={`social-img-${img.alt}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
            <div className="flex items-center justify-center gap-6">
              <a
                href="https://instagram.com/mealhoster"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow font-medium text-gray-800"
                data-testid="instagram-link"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                Instagram
              </a>
              <a
                href="https://tiktok.com/@mealhoster"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow font-medium text-gray-800"
                data-testid="tiktok-link"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.26a8.28 8.28 0 004.76 1.5V7.32a4.85 4.85 0 01-1-.63z"/></svg>
                TikTok
              </a>
            </div>
          </div>
        </section>
      )}

      {viewMode !== 'map' && <Footer />}
    </div>
  );
};

export default HomePage;
